"use client";

import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import { domainMaturityApi, DVR_DEFAULT_ENTITY_ID, type DomainMaturityOverview } from "@/lib/api";
// The old weighted-rollup dashboard API (domainsDashboardApi, backed by the
// now-removed DomainScoreSnapshot model) is gone. Every figure on this page
// — the two headline KPIs, Open Gaps, and the domain snapshot grid — now
// reads from domain_maturity/scoring.py:compute_overall_scores via
// domainMaturityApi.getOverview() below.

const C = {
  ink: "#0f1b2e",
  muted: "#6b7686",
  line: "#e7eaf0",
  blue: "#2563eb",
  blueLight: "#eaf1ff",
  teal: "#0ea5b7",
  tealLight: "#e6fbfb",
  green: "#17a673",
  greenLight: "#e6f8f1",
  amber: "#e2921f",
  amberLight: "#fdf1e0",
  red: "#e6483a",
  redLight: "#fdeae8",
};

const CARD_SHADOW = "0 1px 2px rgba(16,24,40,.04), 0 1px 3px rgba(16,24,40,.04)";

// ─── Icons ──────────────────────────────────────────────────────────────────

function IconDoc({ size = 15, color }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color ?? "currentColor"} strokeWidth={2}>
      <path d="M4 19V5a2 2 0 0 1 2-2h9l5 5v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" />
    </svg>
  );
}

function IconClock({ size = 15, color }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color ?? "currentColor"} strokeWidth={2}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  );
}

function IconAlertCircle({ size = 15, color }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color ?? "currentColor"} strokeWidth={2}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4M12 16h.01" />
    </svg>
  );
}

function IconCheck({ size = 15, color }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color ?? "currentColor"} strokeWidth={2}>
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function IconTrendUp({ size = 11 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
      <polyline points="18 15 12 9 6 15" />
    </svg>
  );
}

function IconTrendDown({ size = 11 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function IconCube({ size = 15, color }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color ?? "currentColor"} strokeWidth={2}>
      <path d="M12 2l9 4.9V17L12 22l-9-5.1V6.9L12 2z" />
      <path d="M12 22V12M21 6.9L12 12 3 6.9" />
    </svg>
  );
}

function IconSparkle({ size = 12, color }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color ?? "currentColor"} strokeWidth={2}>
      <path d="M12 3l1.9 4.9L19 9.8l-4.9 1.9L12 17l-1.9-5.3L5 9.8l5.1-1.9L12 3z" />
    </svg>
  );
}

function IconExternal({ size = 14, color }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color ?? "currentColor"} strokeWidth={2}>
      <path d="M7 17L17 7M7 7h10v10" />
    </svg>
  );
}

function IconTriangleAlert({ size = 15, color }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color ?? "currentColor"} strokeWidth={2}>
      <path d="M12 9v4M12 17h.01M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    </svg>
  );
}

function IconInbox({ size = 15, color }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color ?? "currentColor"} strokeWidth={2}>
      <path d="M3 8l3-4h12l3 4v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />
      <path d="M3 8h18" />
      <path d="M9 12a3 3 0 0 0 6 0" />
    </svg>
  );
}

function IconCalendar({ size = 11, color }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color ?? "currentColor"} strokeWidth={2}>
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
    </svg>
  );
}

// ─── KPI card ───────────────────────────────────────────────────────────────

function KpiCard({
  icon,
  label,
  value,
  trendDir,
  trendText,
  trendSub,
  sparklinePoints,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  trendDir: "up" | "down";
  trendText: string;
  trendSub: string;
  sparklinePoints?: string;
}) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl p-4 pb-4 border bg-white"
      style={{ borderColor: C.line, boxShadow: CARD_SHADOW }}
    >
      <div
        className="absolute top-0 right-0 w-[70px] h-[56px] opacity-50"
        style={{
          backgroundImage: "radial-gradient(circle, #cfe0ff 1.3px, transparent 1.3px)",
          backgroundSize: "8px 8px",
          maskImage: "linear-gradient(to left, black, transparent)",
          WebkitMaskImage: "linear-gradient(to left, black, transparent)",
        }}
      />
      <div className="flex items-center gap-2.5 mb-3.5 relative">
        <div
          className="w-[30px] h-[30px] rounded-[9px] flex items-center justify-center shrink-0"
          style={{ background: C.blueLight, color: C.blue }}
        >
          {icon}
        </div>
        <div className="text-[12.5px] font-semibold" style={{ color: "#48526a" }}>
          {label}
        </div>
      </div>
      <div className="text-[27px] font-extrabold mb-2 relative" style={{ color: "#0b1a33" }}>
        {value}
      </div>
      <div
        className="flex items-center gap-1.5 text-[11.5px] relative"
        style={{ color: trendDir === "up" ? C.green : C.red }}
      >
        {trendDir === "up" ? <IconTrendUp /> : <IconTrendDown />}
        {trendText} <span style={{ color: C.muted }}>{trendSub}</span>
      </div>
      {sparklinePoints && (
        <svg className="absolute right-3.5 bottom-3.5" width={90} height={26} viewBox="0 0 90 26" fill="none">
          <polyline points={sparklinePoints} stroke={C.green} strokeWidth={2} fill="none" />
        </svg>
      )}
    </div>
  );
}

// ─── Panel head ─────────────────────────────────────────────────────────────

function PanelHead({
  icon,
  iconBg,
  iconColor,
  title,
  sub,
  right,
  compact,
}: {
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  title: string;
  sub?: React.ReactNode;
  right?: React.ReactNode;
  compact?: boolean;
}) {
  return (
    <div
      className="flex items-center gap-3 border-b"
      style={{ padding: compact ? "16px 18px" : "18px 20px", borderColor: C.line }}
    >
      <div
        className="rounded-[9px] flex items-center justify-center shrink-0"
        style={{ width: compact ? 28 : 32, height: compact ? 28 : 32, background: iconBg, color: iconColor }}
      >
        {icon}
      </div>
      <div>
        <div className="font-bold" style={{ fontSize: compact ? "13.5px" : "14.5px", color: "#0b1a33" }}>
          {title}
        </div>
        {sub && <div className="mt-0.5">{sub}</div>}
      </div>
      {right && <div className="ms-auto">{right}</div>}
    </div>
  );
}

// ─── Domain grid ────────────────────────────────────────────────────────────

function tierColorForPct(pct: number | null): string {
  if (pct === null) return C.muted;
  if (pct < 35) return C.red;
  if (pct < 65) return C.amber;
  return C.green;
}

function DomainCell({
  name,
  alt,
  pct,
  level,
}: {
  name: string;
  alt: string;
  pct: number | null;
  level: number | null;
}) {
  return (
    <div className="p-4">
      <div className="text-[12px] font-semibold leading-tight" style={{ color: "#20293d" }}>
        {name}
      </div>
      <div className="text-[11px] mt-0.5" style={{ color: "#8892a0" }}>
        {alt}
      </div>
      <div className="flex items-center justify-between mt-3">
        <div className="text-[18px] font-extrabold" style={{ color: tierColorForPct(pct) }}>
          {pct === null ? "—" : `${pct}%`}
        </div>
        <div
          className="text-[10.5px] font-bold rounded-md border px-[7px] py-[2px]"
          style={{ color: "#6b7686", background: "#f1f3f7", borderColor: C.line }}
        >
          {level === null ? "—" : `L${level}`}
        </div>
      </div>
    </div>
  );
}

// ─── Legend row ─────────────────────────────────────────────────────────────

function LegendRow({ color, label, sub, value }: { color: string; label: string; sub: string; value: string }) {
  return (
    <div className="flex items-center justify-between text-[12.5px]">
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
        <span className="font-semibold" style={{ color: "#20293d" }}>
          {label}
          <span className="block text-[10.5px] font-normal mt-0.5" style={{ color: C.muted }}>
            {sub}
          </span>
        </span>
      </div>
      <span className="font-bold" style={{ color: "#20293d" }}>
        {value}
      </span>
    </div>
  );
}

function MiniDonut({ color, dash, value, label }: { color: string; dash: number; value: string; label: string }) {
  return (
    <div className="flex flex-col items-center gap-2 flex-1">
      <svg width={46} height={46} viewBox="0 0 46 46">
        <circle cx="23" cy="23" r="18" fill="none" stroke="#eef0f4" strokeWidth={6} />
        <circle
          cx="23"
          cy="23"
          r="18"
          fill="none"
          stroke={color}
          strokeWidth={6}
          strokeDasharray={`${dash} 113.1`}
          transform="rotate(-90 23 23)"
          strokeLinecap="round"
        />
      </svg>
      <div className="text-[13px] font-extrabold" style={{ color: "#0b1a33" }}>
        {value}
      </div>
      <div className="text-[10px] text-center leading-tight" style={{ color: C.muted }}>
        {label}
      </div>
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const { t, isAr } = useI18n();
  const [overview, setOverview] = useState<DomainMaturityOverview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    domainMaturityApi
      .getOverview(DVR_DEFAULT_ENTITY_ID)
      .then(setOverview)
      .catch((e: unknown) => setLoadError(e instanceof Error ? e.message : String(e)));
  }, []);

  const domains = overview?.domains ?? [];
  const domainsWithGaps = domains.filter((d) => d.gap_count > 0).length;

  // No real per-gap/per-ticket detail endpoint exists yet (only aggregate
  // counts do, via overview above) — these two panels keep their original
  // layout, but with nothing to show, the lists stay empty rather than
  // rendering fabricated example cards. Once a real endpoint exists, filling
  // these in is all it takes for the cards below to render.
  const gaps: Array<{ ref: string; meta: string; date: string; title: string; desc: string; tags: string[] }> = [];

  const tickets: Array<{ status: string; cls: string }> = [];

  return (
    <div className="space-y-5" style={{ padding: "var(--page-padding)", maxWidth: "var(--content-max-width)", margin: "0 auto" }}>
      {/* Page head */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-[24px] font-extrabold" style={{ color: "#0b1a33" }}>
            {t("Dashboard", "لوحة المعلومات")}
          </h1>
          <p className="text-[13.5px] mt-1" style={{ color: C.muted }}>
            {t("Real-time CDO insights from all domains", "رؤى فورية للمسؤول التنفيذي عن البيانات من جميع المجالات")}
          </p>
        </div>
        <button
          type="button"
          className="font-semibold text-[13.5px] px-[18px] py-2.5 rounded-[10px] border"
          style={{ background: C.blueLight, color: C.blue, borderColor: "#cfe0ff" }}
        >
          {t("Export report", "تصدير التقرير")}
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={<IconDoc />}
          label={t("Overall Compliance", "الامتثال الإجمالي")}
          value={overview?.overall_compliance_pct != null ? `${overview.overall_compliance_pct}%` : "—"}
          trendDir="down"
          trendText="—"
          trendSub={
            overview
              ? t(
                  `${overview.contributing_weight_pct}% of index assessed`,
                  `${overview.contributing_weight_pct}% من المؤشر تم تقييمه`
                )
              : t("loading…", "جارٍ التحميل…")
          }
        />
        <KpiCard
          icon={<IconClock />}
          label={t("Overall Maturity", "النضج الإجمالي")}
          value={overview?.overall_maturity_level != null ? `L${overview.overall_maturity_level}` : "—"}
          trendDir="down"
          trendText="—"
          trendSub={
            overview
              ? t(
                  `${overview.contributing_weight_pct}% of index assessed`,
                  `${overview.contributing_weight_pct}% من المؤشر تم تقييمه`
                )
              : t("loading…", "جارٍ التحميل…")
          }
        />
        <KpiCard
          icon={<IconAlertCircle />}
          label={t("Open Gaps", "الفجوات المفتوحة")}
          value={overview ? String(overview.open_gaps_count) : "—"}
          trendDir="down"
          trendText="—"
          trendSub={
            overview
              ? t(`across ${domainsWithGaps} domain(s)`, `في ${domainsWithGaps} مجال`)
              : t("loading…", "جارٍ التحميل…")
          }
        />
        <KpiCard
          icon={<IconCheck />}
          label={t("Audit Readiness", "جاهزية التدقيق")}
          value="0%"
          trendDir="down"
          trendText="0%"
          trendSub={t("vs last month", "مقارنة بالشهر الماضي")}
        />
      </div>

      {/* Domain snapshot */}
      <div className="rounded-2xl border bg-white" style={{ borderColor: C.line, boxShadow: CARD_SHADOW }}>
        <PanelHead
          icon={<IconCube />}
          iconBg={C.blueLight}
          iconColor={C.blue}
          title={t("Domain compliance and maturity snapshot", "لمحة عن الامتثال والنضج حسب المجال")}
          sub={
            <span className="text-[11.5px]" style={{ color: C.muted }}>
              {t(`${domains.length} domains · weighted rollup`, `${domains.length} مجالاً · تجميع مرجّح بالوزن`)}
            </span>
          }
          right={
            <div className="flex gap-[18px] text-[12.5px] font-semibold" style={{ color: C.blue }}>
              <a href="#" className="hover:underline">
                {t("View Compliance", "عرض الامتثال")}
              </a>
              <a href="#" className="hover:underline">
                {t("View Maturity", "عرض النضج")}
              </a>
            </div>
          }
        />
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 divide-x divide-y divide-[#e7eaf0]">
          {domains.map((d) => (
            <DomainCell
              key={d.code}
              name={isAr ? d.name_ar : d.name_en}
              alt={isAr ? d.name_en : d.name_ar}
              pct={d.compliance_pct}
              level={d.maturity_level}
            />
          ))}
          {loadError && (
            <div className="col-span-full p-4 text-[12.5px]" style={{ color: C.red }}>
              {t("Could not load domain scores: ", "تعذّر تحميل درجات المجالات: ")}
              {loadError}
            </div>
          )}
        </div>
      </div>

      {/* Donut row — no real backing endpoint for these yet; every value
          below is neutralized (0 / "—") rather than fabricated. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Evidence Status */}
        <div className="rounded-2xl border bg-white" style={{ borderColor: C.line, boxShadow: CARD_SHADOW }}>
          <PanelHead
            compact
            icon={<IconDoc size={14} />}
            iconBg={C.blueLight}
            iconColor={C.blue}
            title={t("Evidence Status", "حالة الأدلة")}
            sub={
              <span className="flex items-center gap-1 text-[11.5px]" style={{ color: C.green }}>
                <IconTrendUp size={9} /> {t("0% vs last month", "0% مقارنة بالشهر الماضي")}
              </span>
            }
            right={<IconExternal color="#9aa4b8" />}
          />
          <div className="flex flex-col items-center px-[22px] pb-[22px] pt-[18px]">
            <div className="relative w-[170px] h-[170px] my-1.5 mb-[18px]">
              <svg width={170} height={170} viewBox="0 0 170 170">
                <circle cx="85" cy="85" r="68" fill="none" stroke="#eef0f4" strokeWidth={16} />
                <circle cx="85" cy="85" r="68" fill="none" stroke={C.green} strokeWidth={16} strokeDasharray="0 427.3" strokeDashoffset="0" transform="rotate(-90 85 85)" strokeLinecap="round" />
                <circle cx="85" cy="85" r="68" fill="none" stroke={C.blue} strokeWidth={16} strokeDasharray="0 427.3" strokeDashoffset="0" transform="rotate(-90 85 85)" />
                <circle cx="85" cy="85" r="68" fill="none" stroke={C.red} strokeWidth={16} strokeDasharray="0 427.3" strokeDashoffset="0" transform="rotate(-90 85 85)" />
                <circle cx="85" cy="85" r="68" fill="none" stroke={C.amber} strokeWidth={16} strokeDasharray="0 427.3" strokeDashoffset="0" transform="rotate(-90 85 85)" strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-2xl font-extrabold" style={{ color: "#0b1a33" }}>
                  {overview ? overview.evidence_items_count : "—"}
                </div>
                <div className="text-[11px] mt-0.5" style={{ color: C.muted }}>
                  {t("Evidence Items", "عناصر الأدلة")}
                </div>
              </div>
            </div>
            <div className="w-full flex flex-col gap-3">
              <LegendRow color={C.green} label={t("Valid", "سارية")} sub={t("0 items", "0 عنصر")} value="0%" />
              <LegendRow color={C.blue} label={t("Under Review", "قيد المراجعة")} sub={t("0 items", "0 عنصر")} value="0%" />
              <LegendRow color={C.red} label={t("Missing", "مفقودة")} sub={t("0 items", "0 عنصر")} value="0%" />
              <LegendRow color={C.amber} label={t("Expired", "منتهية")} sub={t("0 items", "0 عنصر")} value="0%" />
            </div>
          </div>
        </div>

        {/* Audit Readiness */}
        <div className="rounded-2xl border bg-white" style={{ borderColor: C.line, boxShadow: CARD_SHADOW }}>
          <PanelHead
            compact
            icon={<IconCheck size={14} />}
            iconBg={C.tealLight}
            iconColor={C.teal}
            title={t("Audit Readiness", "جاهزية التدقيق")}
            sub={
              <span className="flex items-center gap-1 text-[11.5px]" style={{ color: C.green }}>
                <IconTrendUp size={9} /> {t("0% vs last month", "0% مقارنة بالشهر الماضي")}
              </span>
            }
            right={<IconExternal color="#9aa4b8" />}
          />
          <div className="flex flex-col items-center px-[22px] pb-[22px] pt-[18px]">
            <div className="relative w-[170px] h-[170px] my-1.5 mb-[18px]">
              <svg width={170} height={170} viewBox="0 0 170 170">
                <circle cx="85" cy="85" r="68" fill="none" stroke="#eef0f4" strokeWidth={16} />
                <circle cx="85" cy="85" r="68" fill="none" stroke={C.amber} strokeWidth={16} strokeDasharray="0 427.3" strokeDashoffset="0" transform="rotate(-90 85 85)" strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-2xl font-extrabold" style={{ color: "#0b1a33" }}>
                  0%
                </div>
                <div className="text-[11px] mt-0.5" style={{ color: C.muted }}>
                  {t("Audit Readiness", "جاهزية التدقيق")}
                </div>
              </div>
            </div>
            <div className="flex gap-3.5 w-full justify-between mb-4">
              <MiniDonut color={C.green} dash={0} value="0%" label={t("Evidence Coverage", "تغطية الأدلة")} />
              <MiniDonut color={C.blue} dash={0} value="0%" label={t("Evidence Quality", "جودة الأدلة")} />
              <MiniDonut color={C.amber} dash={0} value="0%" label={t("Evidence Freshness", "حداثة الأدلة")} />
              <MiniDonut color={C.blue} dash={0} value="0%" label={t("Completeness", "الاكتمال")} />
            </div>
            <div className="w-full rounded-[10px] p-[12px_14px]" style={{ background: C.blueLight }}>
              <div className="flex items-center gap-1.5 text-[11.5px] font-bold mb-2" style={{ color: C.blue }}>
                <IconSparkle /> {t("AI Recommendation", "توصية الذكاء الاصطناعي")}
              </div>
              <div className="flex items-center justify-between text-xs">
                <div>
                  <div className="font-semibold" style={{ color: "#20293d" }}>
                    {t("Top Readiness Gaps", "أبرز فجوات الجاهزية")}
                  </div>
                  <div className="text-[10.5px]" style={{ color: C.muted }}>
                    —
                  </div>
                </div>
                <div>
                  <span className="block text-[10px] text-end" style={{ color: C.muted }}>
                    {t("Impact", "الأثر")}
                  </span>
                  <span className="font-bold text-xs" style={{ color: C.red }}>
                    0%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Gap Analysis */}
        <div className="rounded-2xl border bg-white" style={{ borderColor: C.line, boxShadow: CARD_SHADOW }}>
          <PanelHead
            compact
            icon={<IconAlertCircle size={14} />}
            iconBg={C.blueLight}
            iconColor={C.blue}
            title={t("Gap Analysis", "تحليل الفجوات")}
            sub={
              <span className="flex items-center gap-1 text-[11.5px]" style={{ color: C.green }}>
                <IconTrendUp size={9} /> {t("0% vs last month", "0% مقارنة بالشهر الماضي")}
              </span>
            }
            right={<IconExternal color="#9aa4b8" />}
          />
          <div className="flex flex-col items-center px-[22px] pb-[22px] pt-[18px]">
            <div className="relative w-[170px] h-[170px] my-1.5 mb-[18px]">
              <svg width={170} height={170} viewBox="0 0 170 170">
                <circle cx="85" cy="85" r="68" fill="none" stroke="#eef0f4" strokeWidth={16} />
                <circle cx="85" cy="85" r="68" fill="none" stroke={C.green} strokeWidth={16} strokeDasharray="0 427.3" strokeDashoffset="0" transform="rotate(-90 85 85)" strokeLinecap="round" />
                <circle cx="85" cy="85" r="68" fill="none" stroke={C.blue} strokeWidth={16} strokeDasharray="0 427.3" strokeDashoffset="0" transform="rotate(-90 85 85)" />
                <circle cx="85" cy="85" r="68" fill="none" stroke={C.red} strokeWidth={16} strokeDasharray="0 427.3" strokeDashoffset="0" transform="rotate(-90 85 85)" />
                <circle cx="85" cy="85" r="68" fill="none" stroke="#9aa4b8" strokeWidth={16} strokeDasharray="0 427.3" strokeDashoffset="0" transform="rotate(-90 85 85)" strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-2xl font-extrabold" style={{ color: "#0b1a33" }}>
                  {overview ? overview.open_gaps_count : "—"}
                </div>
                <div className="text-[11px] mt-0.5" style={{ color: C.muted }}>
                  {t("Gaps identified", "الفجوات المحددة")}
                </div>
              </div>
            </div>
            <div className="w-full flex flex-col gap-3">
              <LegendRow color={C.green} label={t("Solved", "تم الحل")} sub={t("0 items", "0 عنصر")} value="0%" />
              <LegendRow color={C.blue} label={t("In Progress", "قيد التنفيذ")} sub={t("0 items", "0 عنصر")} value="0%" />
              <LegendRow color={C.red} label={t("Unaddressed", "غير معالجة")} sub={t("0 items", "0 عنصر")} value="0%" />
              <LegendRow color="#9aa4b8" label={t("Closed", "مغلقة")} sub={t("0 items", "0 عنصر")} value="0%" />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom row — same reasoning: layout preserved, content neutralized. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Critical gaps */}
        <div className="rounded-2xl border bg-white" style={{ borderColor: C.line, boxShadow: CARD_SHADOW }}>
          <PanelHead
            icon={<IconTriangleAlert />}
            iconBg={C.redLight}
            iconColor={C.red}
            title={t("Critical Gaps", "الفجوات الحرجة")}
            right={<IconExternal />}
          />
          {gaps.map((g, i) => (
            <div key={i} className="border rounded-xl m-[14px_20px] p-[16px_18px]" style={{ borderColor: C.line }}>
              <div className="flex items-center justify-between text-[11px] font-semibold mb-2" style={{ color: C.muted }}>
                <span>
                  {g.ref} &nbsp;{g.meta}
                </span>
                <span className="flex items-center gap-1 font-medium">
                  <IconCalendar /> {g.date}
                </span>
              </div>
              <div className="text-[13.5px] font-bold mb-1" style={{ color: "#0b1a33" }}>
                {g.title}
              </div>
              <div className="text-xs mb-3" style={{ color: C.muted }}>
                {g.desc}
              </div>
              <div className="flex gap-2 mb-3.5 flex-wrap">
                {g.tags.map((tag, ti) => (
                  <span
                    key={ti}
                    className="text-[10.5px] font-semibold rounded-md border px-[9px] py-1"
                    style={{ color: "#48526a", background: "#f1f3f7", borderColor: C.line }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <button
                type="button"
                className="w-full font-semibold text-[12.5px] py-[9px] rounded-[9px] border border-dashed flex items-center justify-center gap-1.5"
                style={{ background: C.blueLight, color: C.blue, borderColor: "#b9d1ff" }}
              >
                + {t("Create Ticket", "إنشاء تذكرة")}
              </button>
            </div>
          ))}
        </div>

        {/* Critical & high tickets */}
        <div className="rounded-2xl border bg-white" style={{ borderColor: C.line, boxShadow: CARD_SHADOW }}>
          <PanelHead
            icon={<IconInbox />}
            iconBg={C.blueLight}
            iconColor={C.blue}
            title={t("Critical & high tickets", "التذاكر الحرجة والعالية")}
            right={
              <a href="#" className="flex items-center gap-1.5 text-[12.5px] font-semibold hover:underline" style={{ color: C.blue }}>
                {t("View All", "عرض الكل")} <IconExternal size={12} />
              </a>
            }
          />
          <div>
            {tickets.map((tk, i) => (
              <div key={i} className="flex items-center gap-3 p-[14px_20px] border-b last:border-b-0" style={{ borderColor: C.line }}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2.5 text-[10.5px] font-semibold mb-1" style={{ color: C.muted }}>
                    <span>—</span>
                    <span className="flex items-center gap-1">
                      <IconCalendar /> —
                    </span>
                  </div>
                  <div className="text-[13px] font-bold mb-1.5" style={{ color: "#0b1a33" }}>
                    —
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs" style={{ color: "#48526a" }}>
                      <span
                        className="w-6 h-6 rounded-full shrink-0"
                        style={{ background: "linear-gradient(135deg,#f59e0b,#ef4444)" }}
                      />
                      —
                    </div>
                    <span
                      className="text-[10.5px] font-bold rounded-md px-[9px] py-[3px]"
                      style={
                        tk.cls === "overdue"
                          ? { background: C.redLight, color: C.red }
                          : { background: C.amberLight, color: C.amber }
                      }
                    >
                      {tk.status}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
